'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { DesktopSidebar } from '../header/desktop-sidebar';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { HeaderFavoritesButton } from '../header/header-favorites-button';
import { HeaderProfileButton } from '../header/header-profile-button';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { HeaderCartButton } from '../header/header-cart-button';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '../header/mobile-sidebar';
import { HeaderLanguageMultistore } from '@eliadmin/core/src/components/header/header-language-multistore';
import { useCurrentStore } from '../../utils/store-utils';

export function HeroHeader({
  generalLayout,
  menu,
}: {
  generalLayout: GeneralLayout['data'];
  menu: Menu[];
}) {
  const { currentStore } = useCurrentStore();

  const handleHashScroll = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const url = new URL(href, window.location.href);
    if (!url.hash || url.pathname !== window.location.pathname) return;

    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState(null, '', url.hash);
  };

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'relative z-10 flex w-full flex-col items-center justify-center bg-transparent',
        )}
      >
        <div className={cn('custom-section', 'py-2.5 sm:py-3 md:py-4 lg:py-5 xl:py-6')}>
          <div className={cn('custom-container', 'flex items-center justify-between')}>
            <div className="flex items-center justify-start gap-4">
              <div className="flex items-center justify-center lg:hidden">
                <MobileSidebar menu={menu[0] && menu[0]?.fields} />
              </div>
              <div className="hidden items-center justify-center lg:flex">
                <DesktopSidebar isHomepage={true} />
              </div>
              <div className="hidden items-center justify-center lg:flex">
                <SearchButton variant={'alternative-alt'} />
              </div>
            </div>
            <Link prefetchOnHover href={ROUTES.HOME()}>
              {currentStore === 'najlacnejsie-altanky.sk' ? (
                <Image
                  src={'/images/sk-logo/white-logo.svg'}
                  alt={resolveImageAlt(
                    generalLayout.header?.logo,
                    generalLayout.contact?.company || 'Logo',
                  )}
                  width={480}
                  height={160}
                  quality={100}
                  className="h-[26px] w-auto object-contain object-center sm:h-8 lg:h-12"
                  sizes="(min-width: 1024px) 360px, 240px"
                />
              ) : (
                <Image
                  src={generalLayout.footer?.logo?.url ?? '/images/placeholder.png'}
                  alt={resolveImageAlt(generalLayout?.footer?.logo, generalLayout?.contact?.company || 'Logo')}
                  width={480}
                  height={160}
                  quality={100}
                  className="h-[26px] w-auto object-contain object-center sm:h-8 lg:h-12"
                  sizes="(min-width: 1024px) 360px, 240px"
                />
              )}
            </Link>
            <div className="hidden items-center justify-end gap-2 lg:flex xl:gap-4">
              <HeaderProfileButton isHomepage={true} />
              <HeaderFavoritesButton isHomepage={true} />
              <HeaderCartButton isHomepage={true} />
              <div className="h-[6px] w-[6px] rounded-full bg-white" />
              <HeaderLanguageMultistore isHomepage={true} />
              {/* <LanguageSelect isHomepage={true} /> */}
            </div>
            <div className="flex items-center justify-center lg:hidden">
              <SearchButton variant={'alternative-alt'} />
            </div>
          </div>
        </div>
        <div className={cn('h-[1px] w-full bg-white/10')} />
        <div
          className={cn('custom-section', 'hidden py-4 md:flex', 'ea-header-main__menu-container')}
        >
          <div
            className={cn(
              'custom-container',
              'flex items-center justify-center gap-4 lg:gap-5 xl:gap-6',
            )}
          >
            {menu[0]?.fields.map(field => {
              const href = getSafeHref(field?.path ?? '#');

              return (
                <Link
                  key={field.name}
                  prefetchOnHover
                  href={href}
                  onClick={event => handleHashScroll(event, href)}
                  className={cn('text-sm tracking-tight text-white lg:text-base')}
                >
                  {field.name}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
